import {
  addExact,
  type Exact,
  exactRational,
  exactSqrt,
  formatExact,
  scaleExact,
  toNumberExact,
} from "@/lib/tool-kit/geometry/exact";
import {
  divR,
  fromBigInt,
  intR,
  makeRational,
  type Rational,
  signR,
  subR,
  toNumberR,
} from "@/lib/tool-kit/math/rational";

/**
 * Exact trigonometric values for the angles every course asks for: multiples of 15°.
 *
 * Values for 0°, 15°, 30°, 45°, 60°, 75° and 90° are tabled in full — sin 60° = √3/2,
 * tan 15° = 2 − √3 — and every other multiple of 15° follows from them by the quadrant
 * signs and the reference angle. Anything else has no tidy closed form and is left to
 * the decimal calculation.
 */

export const TRIG_FUNCTIONS = [
  "sin",
  "cos",
  "tan",
  "csc",
  "sec",
  "cot",
] as const;
export type TrigFunction = (typeof TRIG_FUNCTIONS)[number];

/** An exact value, or "undefined" where the function has no value (tan 90°, csc 0°). */
export type TrigValue = Exact | "undefined";
export type ExactTrig = Record<TrigFunction, TrigValue>;

export type Quadrant = 1 | 2 | 3 | 4;

const UNDEFINED = "undefined" as const;
const HALF = divR(intR(1), intR(2));
const QUARTER = divR(intR(1), intR(4));
const THIRD = divR(intR(1), intR(3));
const FULL_TURN = intR(360);
const STEP = intR(15);

const one = exactRational(intR(1));
const zero = exactRational(intR(0));
const two = exactRational(intR(2));
const root = (n: number): Exact => exactSqrt(intR(n));
const over = (value: Exact, num: number, den: number): Exact =>
  scaleExact(value, divR(intR(num), intR(den)));
const negate = (value: Exact): Exact => scaleExact(value, intR(-1));
const sum = (a: Exact, b: Exact): Exact => addExact(a, b);

const SIN_15 = scaleExact(sum(root(6), negate(root(2))), QUARTER);
const COS_15 = scaleExact(sum(root(6), root(2)), QUARTER);

/** The first-quadrant table, indexed by degrees / 15. */
const FIRST_QUADRANT: ExactTrig[] = [
  {
    sin: zero,
    cos: one,
    tan: zero,
    csc: UNDEFINED,
    sec: one,
    cot: UNDEFINED,
  },
  {
    sin: SIN_15,
    cos: COS_15,
    tan: sum(two, negate(root(3))),
    csc: sum(root(6), root(2)),
    sec: sum(root(6), negate(root(2))),
    cot: sum(two, root(3)),
  },
  {
    sin: exactRational(HALF),
    cos: over(root(3), 1, 2),
    tan: scaleExact(root(3), THIRD),
    csc: two,
    sec: over(root(3), 2, 3),
    cot: root(3),
  },
  {
    sin: over(root(2), 1, 2),
    cos: over(root(2), 1, 2),
    tan: one,
    csc: root(2),
    sec: root(2),
    cot: one,
  },
  {
    sin: over(root(3), 1, 2),
    cos: exactRational(HALF),
    tan: root(3),
    csc: over(root(3), 2, 3),
    sec: two,
    cot: scaleExact(root(3), THIRD),
  },
  {
    sin: COS_15,
    cos: SIN_15,
    tan: sum(two, root(3)),
    csc: sum(root(6), negate(root(2))),
    sec: sum(root(6), root(2)),
    cot: sum(two, negate(root(3))),
  },
  {
    sin: one,
    cos: zero,
    tan: UNDEFINED,
    csc: one,
    sec: UNDEFINED,
    cot: zero,
  },
];

/** The angle brought into [0°, 360°). */
export function normalizeDegrees(degrees: Rational): Rational {
  const turns = degrees.num / (degrees.den * BigInt(360));
  const floorTurns =
    signR(degrees) < 0 && turns * degrees.den * BigInt(360) !== degrees.num
      ? turns - BigInt(1)
      : turns;
  return subR(degrees, makeRational(floorTurns * BigInt(360)));
}

/** Quadrant I is [0°, 90°], II is (90°, 180°], III is (180°, 270°], IV is (270°, 360°). */
export function quadrantOf(degrees: Rational): Quadrant {
  const angle = toNumberR(normalizeDegrees(degrees));
  if (angle <= 90) {
    return 1;
  }
  if (angle <= 180) {
    return 2;
  }
  return angle <= 270 ? 3 : 4;
}

/** The acute angle (or 0° / 90° on an axis) the terminal side makes with the x-axis. */
export function referenceAngle(degrees: Rational): Rational {
  const angle = normalizeDegrees(degrees);
  switch (quadrantOf(angle)) {
    case 1:
      return angle;
    case 2:
      return subR(intR(180), angle);
    case 3:
      return subR(angle, intR(180));
    default:
      return subR(FULL_TURN, angle);
  }
}

/** Which functions are positive in each quadrant (the ASTC rule). */
export const POSITIVE_IN_QUADRANT: Record<Quadrant, readonly TrigFunction[]> = {
  1: ["sin", "cos", "tan", "csc", "sec", "cot"],
  2: ["sin", "csc"],
  3: ["tan", "cot"],
  4: ["cos", "sec"],
};

function signed(value: TrigValue, positive: boolean): TrigValue {
  if (value === UNDEFINED || positive) {
    return value;
  }
  return negate(value);
}

/**
 * Exact values of all six functions, or null when the angle is not a multiple of 15°.
 * Pass the angle in degrees as an exact rational.
 */
export function exactTrigValues(degrees: Rational): ExactTrig | null {
  const angle = normalizeDegrees(degrees);
  const reference = referenceAngle(angle);
  const steps = divR(reference, STEP);
  if (steps.den !== BigInt(1)) {
    return null;
  }
  const row = FIRST_QUADRANT[Number(steps.num)];
  if (!row) {
    return null;
  }
  const positive = POSITIVE_IN_QUADRANT[quadrantOf(angle)];
  const result = {} as ExactTrig;
  for (const fn of TRIG_FUNCTIONS) {
    result[fn] = signed(row[fn], positive.includes(fn));
  }
  return result;
}

/** The decimal value of a function at an angle in degrees; null where it is undefined. */
export function trigDecimal(fn: TrigFunction, degrees: number): number | null {
  const radians = (degrees * Math.PI) / 180;
  const sin = Math.sin(radians);
  const cos = Math.cos(radians);
  const tiny = 1e-12;
  switch (fn) {
    case "sin":
      return sin;
    case "cos":
      return cos;
    case "tan":
      return Math.abs(cos) < tiny ? null : sin / cos;
    case "csc":
      return Math.abs(sin) < tiny ? null : 1 / sin;
    case "sec":
      return Math.abs(cos) < tiny ? null : 1 / cos;
    default:
      return Math.abs(sin) < tiny ? null : cos / sin;
  }
}

/** Text of an exact trig value: "√3/2", "−1", "undefined". */
export function formatTrigValue(
  value: TrigValue,
  undefinedText = "undefined"
): string {
  return value === UNDEFINED ? undefinedText : formatExact(value);
}

export function trigValueNumber(value: TrigValue): number | null {
  return value === UNDEFINED ? null : toNumberExact(value);
}

/** An angle as a whole number of 15° steps, or null: for tables of special angles. */
export function degreesFromSteps(steps: number): Rational {
  return fromBigInt(BigInt(steps * 15));
}
